<script setup>
import { computed, useSlots } from 'vue';

import { HrSkeleton, HrTooltip } from '../primitives';
import HrActionBar from './HrActionBar.vue';
import HrIconActionBtn from './HrIconActionBtn.vue';

/**
 * Records section — the layout shell around a collection of records and everything that acts on it.
 *
 * That is the actions operating on the collection, the saved-view selector, search and view
 * controls, filters, and the table itself.
 *
 * Layout only. The section owns the order of its regions and the spacing between them; everything
 * inside a region is the caller's, and it holds no data and runs no behavior. That is what lets the
 * table be app code — a filtering, exporting, view-saving table reaches stores and Firestore, which
 * the design system may not, so it arrives through `body` rather than being rendered here.
 *
 * **The action slots are named for what an action changes, not for where it sits or how much it
 * matters.** A name like `primary-action` invites an argument about what deserves to be primary,
 * and a name like `actions` stops no one putting anything in it. `create` does not: nobody can
 * argue that importing records is not creating them.
 *
 * | Slot | After this action, what is different |
 * | --- | --- |
 * | `create` | There are more records |
 * | `act` | Existing records are different, or records went out |
 * | `view` | Only my screen is different |
 * | `configure` | Future records will have a different shape |
 *
 * **The description belongs to the collection, not the page.** It sits under the name, inside the
 * header, because it says what these records are and what the reader is here to do with them.
 * Left above the section it reads as page copy that happens to precede a table, and nothing
 * carries it when the section moves into a dialog.
 *
 * **It is not a page.** Breadcrumbs say where you are and stay with the page, which keeps this
 * usable inside a dialog. The header is the other way round: the title names the collection being
 * shown, and creating and configuring act on the records in it rather than on the page around it.
 *
 * **Export is a prop, not a slot.** It is the current view serialized: always the whole filtered
 * set and the columns on screen, never a subset picked by eye, and never a format menu. That makes
 * it the one action with no scope question left to answer, so the section renders it and takes it
 * away on selection rather than trusting each page to. The label arrives composed, because a layout
 * component has no business translating; what the section owns is that it appears and disappears in
 * the same places everywhere.
 *
 * It is drawn as an icon with its name in a tooltip, at every width. It is among the rarest things
 * a reader does here — a spreadsheet of what is on screen, occasionally — and a labelled button for
 * it competes with the one control on the page that should win.
 *
 * A collection that cannot be exported as things stand keeps the control and names the reason
 * (`exportUnavailableReason`) rather than losing it. Absent is for what a reader cannot do at all;
 * disabled is for what they could do after undoing something they did themselves.
 *
 * **Compact containers anchor to the viewport, which is why `standalone` exists.** At a phone width
 * the selection bar floats above the bottom edge rather than sitting in the flow. That is right for
 * a section that is the screen, and wrong for one inside a dialog, where a bar pinned to the
 * viewport escapes the surface it belongs to. The section cannot tell which it is, so the caller
 * says, and it passes the answer to HrActionBar, which owns the floating. The safe value is the
 * falsy one: without it nothing floats anywhere.
 *
 * **Browse mode and selection mode.** The selection bar is inserted above the body, never swapped
 * in for the toolbar. Swapping is a compact-width habit that does not survive a wide screen, where
 * there is room for both and a row of controls vanishing on selection reads as a fault. `view` and
 * `search` are not actions, so they persist across both modes; `create` belongs to browse mode,
 * having nothing to narrow to.
 *
 * **Regions are absent, not empty.** A region whose slot was never passed renders no element and
 * occupies no height, so a section never shows a disabled control or a divider with nothing under
 * it. `filters` is the exception by design: it renders whenever it is passed, because the
 * add-filter chip is the entry point and the row has to exist before there is anything in it.
 *
 * @status recommended
 * @group layout
 * @useWhen Framing a collection of records with the actions, search, filters and selection that
 *   operate on it, on a page or inside a dialog
 * @avoidWhen The screen shows a single record rather than a collection
 * @related HrDataTable
 */
defineOptions({ name: 'HrRecordsSection' });

const props = defineProps({
  /**
   * What the section has to show in place of the records. `empty` and `empty-filtered` each swap
   * `body` for the slot of the same name; the regions around it stay as they are.
   *
   * They are two states rather than one because they are two different problems. `empty` is a
   * collection nobody has filled, and the way out of it is to fill it. `empty-filtered` is a
   * collection with records in it that the reader cannot currently see, and the way out is to stop
   * hiding them. Telling somebody to add their first record when they have four hundred and a typo
   * in the search field is the failure this exists to prevent.
   * `pending` is the state before any of that is known: the section is resolving which records it
   * is even meant to show, because the reader left a saved view open and it has not come back yet.
   * Standing in for it with the collection's defaults means showing one set of records and then
   * replacing it, so this holds the place instead. It is not the same as the records loading, which
   * is the table's own business and says nothing about which of them were asked for.
   * @values ready, pending, empty, empty-filtered
   */
  state: {
    type: String,
    default: 'ready',
    validator: (value) => ['ready', 'pending', 'empty', 'empty-filtered'].includes(value),
  },
  /**
   * This section is the screen's main content rather than part of something else, so its compact
   * containers may anchor to the viewport.
   *
   * A dialog leaves it off: a selection bar pinned to the bottom of the window would float free of
   * the dialog that owns it, over whatever happens to be behind.
   */
  standalone: {
    type: Boolean,
    default: false,
  },
  /**
   * Label for the export control, with its count already in it — "Export all 148 records". Null
   * when the collection cannot be exported at all.
   *
   * The count belongs in the label because "all" on a filtered table is a word the reader has to
   * interpret. The control is absent while rows are selected: export is the whole filtered set, so
   * a selection has nothing to say about it.
   * @example Export all 148 records
   */
  exportLabel: {
    type: String,
    default: null,
  },
  /**
   * The export is waiting on something the section cannot see — a saved view still arriving, say.
   * Waiting rather than disabled: what it would write is the set the section is about to be told to
   * replace, and a greyed control says the reader may not export, which is a different thing.
   */
  exportWaiting: {
    type: Boolean,
    default: false,
  },
  /**
   * Why exporting would not do what its name promises as things stand, phrased as the way out of
   * it — "Clear the search to export". The control is disabled and takes this as its accessible
   * name, rather than being taken away: a control that disappears from the row reads as a fault,
   * and this is a state the reader put themselves in and can leave.
   *
   * Null when nothing is in the way.
   * @example Clear the search to export
   */
  exportUnavailableReason: {
    type: String,
    default: null,
  },
  /**
   * The create control floats over the screen instead of sitting in the header, as it does at a
   * phone width on a section that is the screen. It then holds no place in the header, and a header
   * left with nothing in it is not drawn: a floating control kept in the flow left an empty band
   * above the search.
   */
  floatingCreate: {
    type: Boolean,
    default: false,
  },
  /**
   * The create control acts on the records, the selected ones when there are any, so it stays put
   * through selection rather than stepping out. Without it the same verb would leave the header and
   * come back in the selection bar in another style, and a reader watching for it loses it.
   */
  createTakesSelection: {
    type: Boolean,
    default: false,
  },
  /**
   * Records are selected, so the selection bar is inserted above the body and `create` steps out,
   * unless it takes the selection.
   *
   * Selection is orthogonal to `state` — a section can hold a selection while the next page of
   * records is loading — which is why it is its own flag rather than a second state.
   */
  selecting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the reader asks for the current view as a file. */
  export: null,
});

const slots = useSlots();

/*
 * Enough to read as a list rather than as one thing still arriving. The number is not a guess at
 * how many records there are, which is not knowable yet; it is how much page to hold still.
 */
const PENDING_ROW_COUNT = 6;

/*
 * Each state has its own slot, and a state whose slot was never passed renders nothing at all —
 * regions here are absent rather than empty. That leaves the caller to report a state only when it
 * has something to put in it, which is also the only way it can know whether it does.
 */
const bodySlot = computed(() => {
  if (props.state === 'pending') return 'pending';
  if (props.state === 'empty') return 'empty';
  if (props.state === 'empty-filtered') return 'empty-filtered';
  return 'body';
});

/*
 * Pending is the one state the section draws itself. The other two are the caller's to word — only
 * it knows what an empty collection of these records means — but a placeholder for records nobody
 * has seen yet says the same thing on every page, and asking each one to supply it would mean a
 * page that forgot had no held place at all.
 */
const isPending = computed(() => props.state === 'pending');

// The body may be standing in, or only the export may be waiting; either leaves it unable to write.
const isExportWaiting = computed(() => isPending.value || props.exportWaiting);

const hasBody = computed(() => isPending.value || !!slots[bodySlot.value]);

// Creating has nothing to narrow to, so it belongs to browse mode alone; so does exporting, which
// is always the whole filtered set and so has nothing to say about a selection.
const showsCreate = computed(() => !!slots.create && (!props.selecting || props.createTakesSelection));
const showsHeaderCreate = computed(() => showsCreate.value && !props.floatingCreate);
const showsExport = computed(() => props.exportLabel !== null && !props.selecting);
const hasToolbar = computed(() => !!slots.search || !!slots.filters || !!slots.view);
/*
 * Export with nothing else in the header, as in a dialog that lists records for a decision. In the
 * header it would hold a row of its own above the search, so it joins the toolbar's trailing end.
 * Anywhere the header has more to show, export stays with it, away from the unbounded filter row.
 */
const exportsFromToolbar = computed(() => showsExport.value
  && hasToolbar.value
  && !slots.title && !slots.description
  && !showsHeaderCreate.value && !slots.configure && !slots.manage);
const hasHeaderActions = computed(() => showsHeaderCreate.value
  || (showsExport.value && !exportsFromToolbar.value)
  || !!slots.configure || !!slots.manage);
const hasSelectionBar = computed(() => props.selecting && (!!slots.selection || !!slots.act));

/*
 * Something is floating over the records, so the records need somewhere to end that is not behind
 * it. Without the clearance the last row and the pagination sit under a control that cannot be
 * scrolled away from, which is the one thing a floating control must not cost.
 */
const reservesFloatingRoom = computed(() => (
  props.standalone && (!!slots.create || hasSelectionBar.value)
));
</script>

<template>
  <div :class="['hr-records-section', reservesFloatingRoom && 'hr-records-section--floating']">
    <div
      v-if="$slots.title || hasHeaderActions || $slots.description"
      class="hr-records-section__header"
    >
      <div class="hr-records-section__header-text">
        <div
          v-if="$slots.title"
          class="hr-records-section__title"
        >
          <!-- A plain name, or a saved-view selector when the reader can switch between views. -->
          <!--
            Left out when the breadcrumb or the tab the section sits in already names the collection,
            which is most of the time: repeating it puts the same words twice down the same screen.
            It earns its place when the section is one of several on a page, or when it carries the
            saved-view selector and so has to be a control rather than a heading.
          -->
          <!-- @slot The collection's name, at the leading end. -->
          <slot name="title" />
        </div>

        <!--
          Under the name rather than above the section, because it is the collection's own: what these
          records are and what the reader is here to do with them. Above it, it reads as page copy that
          happens to precede a table, and nothing carries it when the section moves into a dialog.
        -->
        <div
          v-if="$slots.description"
          class="hr-records-section__description"
        >
          <!-- @slot What this collection is, in a sentence or two, under its name. -->
          <slot name="description" />
        </div>
      </div>

      <!--
        Gathered in a box of their own rather than left loose in the header row. What sits in the
        title region can grow a second row under itself — a saved view's actions do — and controls
        centred on the header would then drift down the height of a row they have nothing to do
        with. This box holds the height of the first row and centres them in that.
      -->
      <div
        v-if="hasHeaderActions"
        class="hr-records-section__header-actions"
      >
        <!--
          At the leading end, which is the least prominent place in this row: these are rare and
          mostly administrative, and the trailing end belongs to the control that makes records.
        -->
        <!-- @slot What can be done to the whole collection, needing nothing selected. One menu. -->
        <slot name="manage" />
        <!-- @slot Settings that change the shape of records made from here on. One control. -->
        <slot name="configure" />
        <!--
          In the header rather than beside the filters it describes. The filter row has no bound on
          its width — one chip can read "Gender: Female or Other or Male" — so a control pinned to its
          trailing end needs the row to become a scrolling sub-region with masked edges. The header is
          bounded and cannot overflow that way.
        -->
        <!--
          A Material Symbols name, so it carries the sym_o_ prefix: the app's default icon set is
          the classic one, which has no file_export to render and would draw the name as text.
        -->
        <!--
          Waiting while pending, rather than taken away and put back, and waiting rather than
          disabled: what it would write is the set the section is about to be told to replace, but
          a greyed control says the reader may not export, which is a different thing from not yet.
        -->
        <hr-icon-action-btn
          v-if="showsExport && !exportsFromToolbar"
          icon="sym_o_file_export"
          :aria-label="exportUnavailableReason ?? exportLabel"
          :unavailable="exportUnavailableReason !== null"
          :loading="isExportWaiting"
          :aria-busy="isExportWaiting ? 'true' : undefined"
          data-e2e="button-export-table"
          @click="emit('export')"
        >
          <hr-tooltip>{{ exportUnavailableReason ?? exportLabel }}</hr-tooltip>
        </hr-icon-action-btn>
        <template v-if="showsHeaderCreate">
          <!--
            Sibling creation paths — import, a bulk invite, a copy — belong in its menu rather
            than beside it as controls of their own.
          -->
          <!-- @slot The one control that makes more records. -->
          <slot name="create" />
        </template>
      </div>
    </div>

    <div
      v-if="hasToolbar"
      class="hr-records-section__toolbar"
    >
      <div
        v-if="$slots.search"
        class="hr-records-section__search"
      >
        <!-- @slot Search field. Not an action, so it stays through selection. -->
        <slot name="search" />
      </div>

      <!--
        Absent while pending, rather than the one region that renders whenever it is passed. The
        chips name the filters this section is about to be told to replace, and a reader who watches
        them change has been told twice what is narrowing their records, wrongly the first time.
      -->
      <div
        v-if="$slots.filters && !isPending"
        class="hr-records-section__filters"
      >
        <!-- @slot Applied filter chips, add filter, clear all. -->
        <slot name="filters" />
      </div>

      <div
        v-if="$slots.view"
        class="hr-records-section__view"
      >
        <!--
          Density, saved views, anything that writes nothing. Not actions, so they stay through
          selection.
        -->
        <!-- @slot Controls that change only what is on screen. -->
        <slot name="view" />
      </div>

      <hr-icon-action-btn
        v-if="exportsFromToolbar"
        class="hr-records-section__toolbar-export"
        icon="sym_o_file_export"
        :aria-label="exportUnavailableReason ?? exportLabel"
        :unavailable="exportUnavailableReason !== null"
        :loading="isExportWaiting"
        :aria-busy="isExportWaiting ? 'true' : undefined"
        data-e2e="button-export-table"
        @click="emit('export')"
      >
        <hr-tooltip>{{ exportUnavailableReason ?? exportLabel }}</hr-tooltip>
      </hr-icon-action-btn>
    </div>

    <!--
      Inserted above the body rather than put in the toolbar's place. A section must be able to show
      a selection that has nothing to do with it: a page whose selection IS its stored value has a
      count and a clear and no actions at all.
    -->
    <hr-action-bar
      v-if="hasSelectionBar"
      class="hr-records-section__selection"
      :standalone="standalone"
    >
      <template
        v-if="$slots.selection"
        #leading
      >
        <!-- @slot What is selected, and how to clear it. -->
        <slot name="selection" />
      </template>
      <!--
        Named rather than loose. A bare outlet counts as default-slot content whether or not the
        caller passed anything, so the bar drew an empty actions container for a selection that has
        no actions.
      -->
      <template
        v-if="$slots.act"
        #default
      >
        <!--
          Acts first, then a separator, then the ones that send records out and change nothing:
          ending on the safe ones keeps a destructive Act away from the overflow, where a mis-tap
          lands.
        -->
        <!-- @slot What can be done to the selected records, and what can be taken out of them. -->
        <slot name="act" />
      </template>
    </hr-action-bar>

    <div
      v-if="hasBody"
      class="hr-records-section__body"
    >
      <template v-if="bodySlot === 'pending'">
        <hr-skeleton
          v-for="row in PENDING_ROW_COUNT"
          :key="row"
          class="hr-records-section__pending-row"
          type="rect"
        />
      </template>
      <template v-else-if="bodySlot === 'empty'">
        <!-- @slot Empty state for a collection with no records in it at all. -->
        <slot name="empty" />
      </template>
      <template v-else-if="bodySlot === 'empty-filtered'">
        <!-- @slot Empty state for a search or filter that matches nothing. -->
        <slot name="empty-filtered" />
      </template>
      <template v-else>
        <!-- @slot The records — a table, a card grid, or a board. -->
        <slot name="body" />
      </template>
    </div>

    <slot
      v-if="showsCreate && floatingCreate"
      name="create"
    />
  </div>
</template>

<style lang="scss" scoped>
.hr-records-section {
  display: flex;
  flex-direction: column;
  gap: var(--app-comp-records-section-region-gap);
}

/*
 * Row-shaped and row-sized, so the records land in the height the placeholder was already holding
 * rather than pushing the page down as they arrive.
 */
.hr-records-section__pending-row {
  height: var(--app-comp-records-section-pending-row-height);
}

.hr-records-section__pending-row + .hr-records-section__pending-row {
  margin-top: var(--app-comp-records-section-pending-row-gap);
}

.hr-records-section__filters,
.hr-records-section__view {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--app-comp-records-section-actions-gap);
}

/*
 * One line, never two. Left to wrap, a control drops below the title rather than the title giving
 * up the width — and the title is the only thing here that can afford to, because it truncates and
 * the controls do not.
 */
/*
 * Top-aligned, not centred. The title region is the one part that can grow a second row, and the
 * controls belong with the first one; centring pulled them down by half of whatever appeared
 * underneath. Both sides then hold the row's height and centre within it, so they line up whether
 * or not anything wrapped.
 */
.hr-records-section__header {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: var(--app-comp-records-section-actions-gap);
}

/*
 * The name and the sentence under it are one block, and the controls sit beside the block rather
 * than on the name's row. A section whose breadcrumb or tab already names it leaves the title out,
 * and when the controls were tied to that row they were left alone on it with the description
 * underneath, which put an empty column where the name used to be.
 *
 * This is what grows into the free space, so the controls end up at the trailing edge whether or
 * not there is a title. An auto margin on the controls would do the same thing visually, but it
 * takes the free space before flex-grow can reach it, and this box would stop being a measure of
 * anything for the title region inside it.
 */
.hr-records-section__header-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  /*
   * Its own value rather than the gap that spaces controls along a row. The name above is a control
   * with its own box, so a control-sized gap measured from that box's edge reads tighter than the
   * same number does between two buttons.
   */
  gap: var(--app-comp-records-section-description-gap);
}

/*
 * Held to a reading measure rather than the section's width, and in the muted role: it introduces
 * the records, so it must not compete with the name above it or the column headers below.
 */
.hr-records-section__description {
  max-width: var(--app-comp-records-section-description-measure);
  color: var(--app-on-surface-variant);
}

/*
 * The region spaces itself, so what is put in it must not bring its own. Quasar gives every
 * paragraph a 16px bottom margin, which landed on top of the gap under this region: the sentence
 * sat tight under the name with twice as much room beneath it as above, and nothing in either
 * component had asked for that.
 */
.hr-records-section__description > :deep(*) {
  margin-block: 0;
}

.hr-records-section__header-actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--app-comp-records-section-actions-gap);
  min-height: var(--app-comp-records-section-header-row-height);
}

.hr-records-section__header-actions > * {
  flex: none;
}

/*
 * Search, filters and export on one line: they are one thought — what subset am I looking at — and
 * splitting them put a row's height between a filter and the field that narrows the same set.
 *
 * Aligned to the top rather than the middle, so the view controls stay on the first line as a long
 * filter set wraps beneath them.
 */
.hr-records-section__toolbar {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: var(--app-comp-records-section-actions-gap);
}

/*
 * The title holds the leading end and the header's controls gather at the trailing end. Split
 * across the row they read as two unrelated groups rather than one set of things to do here.
 *
 * The title is the one thing here with no bound on its width — it is a saved view's name, and a
 * reader can call a view anything — so it is the one thing that shrinks. The controls beside it
 * keep their size; a truncated verb is worse than a truncated noun.
 */
/*
 * A container, so whatever is put in the title region can answer to the room the region actually
 * has. Window width is the wrong measure and parts company with this one the moment a navigation
 * drawer opens, or the section is placed in a dialog. Unnamed on purpose: what is in the slot asks
 * about the space it is in without having to know whose space it is.
 *
 * The auto margin that used to push the trailing controls over went with it. An auto margin takes
 * the free space before flex-grow can reach it, which left this box sized to its content and so
 * worth nothing as a measure of anything. Growing into that same space puts the controls where the
 * margin did.
 */
.hr-records-section__title {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: var(--app-comp-records-section-header-row-height);
  container-type: inline-size;
}

// Whatever is in the slot takes the region's width, rather than being sized by its own content.
.hr-records-section__title > * {
  flex: 1 1 auto;
  min-width: 0;
}

// So a filter set that scrolls sideways has a width to scroll within rather than widening the page.
.hr-records-section__filters {
  min-width: 0;
}

.hr-records-section__toolbar-export {
  flex: none;
  margin-inline-start: auto;
}

// The toolbar and the selection bar are both present at once, and hold the same minimum height.
.hr-records-section__toolbar {
  min-height: var(--app-comp-records-section-toolbar-min-height);
}

/*
 * Enough width for a search term and no more. It used to take the row because it had the row to
 * itself; sharing with the filters, every pixel it holds is one they do not.
 */
.hr-records-section__search {
  flex: 0 1 var(--app-comp-records-section-search-width);
  min-width: 0;
}

/*
 * Takes the room left over. It used to be sized by its own chips, to keep export beside them; export
 * has since moved to the header, and being content-sized left the filter set measuring how many
 * chips fit inside a box that was only as wide as the chips it had already decided to show.
 *
 * The min-height is what aligns them: a chip is 32px against a 40px search field, and the row is
 * top-aligned so the view controls stay on the first line as chips wrap beneath. Giving the block
 * the row's height lets its own centring do the work, rather than the two rules fighting.
 */
/*
 * Fills the row, so its width is the row's business and not the chips'. Content-sized, it grew and
 * shrank with the very set it was being used to measure, and the count flipped back and forth
 * forever. The overflow chip hugs the chips from inside the filter set instead.
 *
 * This is why the row does not wrap above: wrapping is decided before shrinking, so a content-sized
 * filter set offers its whole natural width as the size to fit and breaks to its own line instead
 * of clipping. With nowrap there is no line to break to and it simply shrinks.
 */
.hr-records-section__filters {
  flex: 1 1 0;
  min-width: 0;
  min-height: var(--app-comp-records-section-toolbar-min-height);
}

.hr-records-section__view {
  flex: none;
  margin-inline-start: auto;
}

/*
 * Stacked one breakpoint later than the rest of the compact treatment. Everything else here follows
 * the create control down to a phone width, but the app's navigation drawer is on screen from the
 * small breakpoint up and takes its width off the row — so a tablet has a header to put the create
 * button in and still no room for search and filters to share a line.
 */
/*
 * The filters wrap below, and the view controls and export stay beside the search. Left in source
 * order they each took a line of their own after the filters, where an icon alone on a row reads as
 * stray rather than as part of the toolbar.
 */
@media (max-width: 1023px) {
  .hr-records-section__toolbar {
    flex-wrap: wrap;
  }

  .hr-records-section__search {
    flex: 1 1 0;
  }

  .hr-records-section__filters {
    flex: 1 1 100%;
    order: 1;
  }
}

// No padding of its own: a table in here bleeds to the page edges on small screens by cancelling
// --hr-page-padding, which only works while nothing sits between it and the page.
.hr-records-section__body {
  min-width: 0;
}

/*
 * Room at the end of the records for whatever is floating over them. Only at the width where
 * anything floats, and only tall enough to clear the largest of them plus the gap it keeps.
 */
@media (max-width: 599px) {
  .hr-records-section--floating {
    padding-block-end: var(--app-comp-records-section-floating-clearance);
  }
}

</style>
